import { useEffect, useState, useRef, useCallback } from 'react';
import { Upload, X, Download, FileText, FileImage, FileSpreadsheet, FileVideo, FileAudio, Archive, File, ZoomIn, CheckCircle } from 'lucide-react';
import api from '../utils/api';

const S3_DIRECT = 'https://prinmann-shop-uploads.s3.ap-northeast-2.amazonaws.com/';
function resolveUrl(filename) {
  if (!filename) return '';
  if (filename.startsWith('/api/s3/proxy')) return filename;
  if (filename.startsWith(S3_DIRECT)) {
    return `/api/s3/proxy?key=${encodeURIComponent(filename.slice(S3_DIRECT.length))}`;
  }
  if (filename.startsWith('https://')) return filename;
  return `/uploads/attachments/${filename}`;
}

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

function FileIcon({ mimetype, size = 18 }) {
  if (mimetype.startsWith('image/')) return <FileImage size={size} className="text-blue-400" />;
  if (mimetype === 'application/pdf') return <FileText size={size} className="text-red-400" />;
  if (mimetype.includes('spreadsheet') || mimetype.includes('excel') || mimetype.includes('csv'))
    return <FileSpreadsheet size={size} className="text-green-400" />;
  if (mimetype.includes('video')) return <FileVideo size={size} className="text-purple-400" />;
  if (mimetype.includes('audio')) return <FileAudio size={size} className="text-yellow-400" />;
  if (mimetype.includes('zip') || mimetype.includes('rar') || mimetype.includes('tar') || mimetype.includes('7z'))
    return <Archive size={size} className="text-orange-400" />;
  return <File size={size} className="text-gray-400" />;
}

/* ── 업로드 진행 모달 ── */
function UploadProgressModal({ files }) {
  const all = files.length;
  const done = files.filter(f => f.pct === 100).length;
  const totalPct = all > 0 ? Math.round(files.reduce((s, f) => s + f.pct, 0) / all) : 0;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl w-80 p-5 flex flex-col gap-4">
        {/* 헤더 */}
        <div className="flex items-center gap-2">
          <Upload size={15} className="text-[#e0005e]" />
          <span className="text-white text-sm font-semibold">업로드 중</span>
          <span className="text-gray-500 text-xs ml-auto">{done}/{all}</span>
        </div>

        {/* 전체 바 */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-gray-400 text-xs">전체 진행률</span>
            <span className="text-[#e0005e] text-xs font-semibold">{totalPct}%</span>
          </div>
          <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-[#e0005e] rounded-full transition-all duration-300"
              style={{ width: `${totalPct}%` }}
            />
          </div>
        </div>

        {/* 파일별 목록 */}
        <div className="flex flex-col gap-2 max-h-48 overflow-y-auto">
          {files.map((f, i) => (
            <div key={i} className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <span className="text-gray-300 text-[11px] truncate flex-1 mr-2">{f.name}</span>
                {f.pct === 100
                  ? <CheckCircle size={12} className="text-emerald-400 flex-shrink-0" />
                  : <span className="text-gray-500 text-[11px] flex-shrink-0">{f.pct}%</span>
                }
              </div>
              <div className="h-1 bg-gray-700 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-300 ${f.pct === 100 ? 'bg-emerald-400' : 'bg-[#e0005e]'}`}
                  style={{ width: `${f.pct}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function FileAttachments({ entityType, entityId, editor, onImageInsert, compact = false, canUpload = true }) {
  const [files, setFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState([]); // [{name, pct}]
  const [dragOver, setDragOver] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const fileInputRef = useRef(null);
  const user = JSON.parse(localStorage.getItem('paper_work_user') || '{}');
  const isSuperadmin = user.role === 'superadmin';

  const load = useCallback(async () => {
    if (!entityId) return;
    const r = await api.get('/files', { params: { entityType, entityId } });
    setFiles(r.data);
  }, [entityType, entityId]);

  useEffect(() => { load(); }, [load]);

  const handleFiles = async (fileList) => {
    if (!entityId) return;
    const fileArray = Array.from(fileList);
    if (!fileArray.length) return;

    // 진행 모달 초기화
    const progressList = fileArray.map(f => ({ name: f.name, pct: 0 }));
    setUploadProgress(progressList);

    try {
      for (let i = 0; i < fileArray.length; i++) {
        const file = fileArray[i];
        const fd = new FormData();
        fd.append('file', file);
        fd.append('entityType', entityType);
        fd.append('entityId', String(entityId));

        const r = await api.post('/files', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => {
            const pct = e.total ? Math.round((e.loaded / e.total) * 100) : 0;
            setUploadProgress(prev => prev.map((p, idx) => idx === i ? { ...p, pct } : p));
          },
        });
        const af = r.data;

        // 이미지는 에디터에 바로 삽입
        if (file.type.startsWith('image/') && (editor || onImageInsert)) {
          const url = resolveUrl(af.filename);
          if (onImageInsert) onImageInsert(url);
          else editor?.chain().focus().setImage({ src: url }).run();
        }

        // 마지막 파일도 100%로 표시
        setUploadProgress(prev => prev.map((p, idx) => idx === i ? { ...p, pct: 100 } : p));
      }
      await load();
    } finally {
      // 잠깐 완료 표시 후 모달 닫기
      setTimeout(() => setUploadProgress([]), 800);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const onDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const onDragLeave = () => setDragOver(false);

  const download = async (af) => {
    const link = document.createElement('a');
    link.href = `/api/files/${af.id}/download`;
    link.click();
    setTimeout(load, 500);
  };

  const open = (af) => {
    if (af.mimetype.startsWith('image/')) {
      setLightbox({ url: resolveUrl(af.filename), name: af.originalname });
    } else if (af.mimetype === 'application/pdf') {
      window.open(resolveUrl(af.filename), '_blank');
    } else {
      download(af);
    }
  };

  const del = async (af) => {
    if (!confirm(`"${af.originalname}" 을(를) 삭제하시겠습니까?`)) return;
    await api.delete(`/files/${af.id}`);
    load();
  };

  const nonImageFiles = files.filter(f => !f.mimetype.startsWith('image/'));
  const imageFiles = files.filter(f => f.mimetype.startsWith('image/'));

  if (!entityId) return null;

  return (
    <div className={compact ? '' : 'border-t border-gray-800 px-5 py-3'}>
      {/* 업로드 진행 모달 */}
      {uploadProgress.length > 0 && <UploadProgressModal files={uploadProgress} />}

      {/* 드래그앤드랍 존 */}
      {canUpload && (
        <>
          <div
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onClick={() => fileInputRef.current?.click()}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed cursor-pointer transition-colors text-xs ${
              dragOver
                ? 'border-[#e0005e] bg-[#e0005e]/10 text-[#e0005e]'
                : 'border-gray-700 text-gray-600 hover:border-gray-500 hover:text-gray-400'
            }`}
          >
            <Upload size={13} />
            <span>파일 드래그 또는 클릭 (이미지는 본문에 자동 삽입)</span>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={e => handleFiles(e.target.files)}
          />
        </>
      )}

      {/* 첨부 파일 목록 (이미지 제외) — 액션버튼을 왼쪽(파일명 앞)에 배치 */}
      {nonImageFiles.length > 0 && (
        <div className="mt-2 space-y-1">
          {nonImageFiles.map(af => (
            <div
              key={af.id}
              className="flex items-center gap-1.5 group px-2 py-1.5 rounded-lg hover:bg-gray-800/50 cursor-pointer"
              onClick={() => open(af)}
            >
              {/* 액션 버튼 — 파일 아이콘 왼쪽에 배치, 호버 시 표시 */}
              <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                <button
                  onClick={e => { e.stopPropagation(); download(af); }}
                  className="p-1 rounded text-gray-400 hover:text-[#e0005e] hover:bg-gray-700"
                  title="다운로드"
                >
                  <Download size={12} />
                </button>
                {(af.uploaderId === user.id || isSuperadmin) && (
                  <button
                    onClick={e => { e.stopPropagation(); del(af); }}
                    className="p-1 rounded text-gray-400 hover:text-red-400 hover:bg-gray-700"
                    title="삭제"
                  >
                    <X size={12} />
                  </button>
                )}
              </div>

              <FileIcon mimetype={af.mimetype} size={16} />

              <div className="flex-1 min-w-0">
                <p className="text-gray-300 text-xs truncate group-hover:text-white transition-colors">{af.originalname}</p>
                <p className="text-gray-600 text-[10px]">
                  {formatSize(af.size)} · 다운로드 {af.downloadCount}회
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 이미지 파일 목록 — 오버레이 버튼은 상단 우측에 배치 */}
      {imageFiles.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {imageFiles.map(af => (
            <div key={af.id} className="relative group cursor-pointer" onClick={() => open(af)}>
              <img
                src={resolveUrl(af.filename)}
                alt={af.originalname}
                className="w-14 h-14 object-cover rounded-md border border-gray-800"
              />
              {/* 버튼을 상단에 배치 — 화면 하단 채팅 버튼과 겹치지 않도록 */}
              <div className="absolute top-0 left-0 right-0 flex justify-end gap-0.5 p-0.5 bg-gradient-to-b from-black/70 to-transparent rounded-t-md opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={e => { e.stopPropagation(); open(af); }} className="p-0.5 text-white hover:text-blue-300" title="크게 보기">
                  <ZoomIn size={11} />
                </button>
                <button onClick={e => { e.stopPropagation(); download(af); }} className="p-0.5 text-white/70 hover:text-white" title="다운로드">
                  <Download size={11} />
                </button>
                {(af.uploaderId === user.id || isSuperadmin) && (
                  <button onClick={e => { e.stopPropagation(); del(af); }} className="p-0.5 text-red-400 hover:text-red-300" title="삭제">
                    <X size={11} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 이미지 라이트박스 */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[9999] bg-black/90 flex flex-col items-center justify-center"
          style={{ padding: '2rem' }}
          onClick={() => setLightbox(null)}
        >
          <div
            className="flex flex-col items-center"
            style={{ maxWidth: '90vw', maxHeight: '90vh' }}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between w-full mb-2 gap-3">
              <p className="text-white/50 text-xs truncate flex-1">{lightbox.name}</p>
              <button
                onClick={() => setLightbox(null)}
                className="flex-shrink-0 flex items-center gap-1 text-white/60 hover:text-white text-xs"
              >
                <X size={14} /> 닫기
              </button>
            </div>
            <img
              src={lightbox.url}
              alt={lightbox.name}
              style={{ maxWidth: '90vw', maxHeight: '80vh', objectFit: 'contain', display: 'block', borderRadius: '8px' }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
